// PageNodePreview.jsx

import React, { useMemo } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatSummaryDate } from 'utilities/DateTime';
import { ComponentType, resolveComponent } from 'utilities/NodeTypeComponents';
import { getNodeTypeLabel } from 'utilities/NodeTypeLabels';

// Hooks
import useGalleryCardFieldFormatting from 'views/gallery/hooks/useGalleryCardFieldFormatting';

// CSS
import './PageNodePreview.css';

// Components
import { normalizePageNodePresentation, PAGE_NODE_PRESENTATION } from './pageNodePresentation';
import GalleryCardRenderer from 'views/gallery/cards/GalleryCardRenderer';
import ListViewItem from 'views/list/item/ListViewItem';
import { buildListViewItemData, resolveListViewItemConfig } from 'views/list/item/listViewItemData';

const GALLERY_PREVIEW_VIEW = {
    includeSubtitle: true,
    properties: ['dateModified'],
};

/**
 * PageNodePreview
 *
 * Renders a single node using a lightweight page block presentation.
 *
 * @param {object|null} node - Node row to preview.
 * @param {function|null} [getTypeOverride] - Optional node type lookup override.
 * @param {'LIST'|'GALLERY'|'FEED'} [presentation='LIST'] - Preview presentation mode.
 * @param {object|null} [typeDefOverride] - Optional node type definition override.
 *
 * @returns {ReactNode} The rendered node preview.
 */
const PageNodePreview = React.memo(({
    getTypeOverride = null,
    node,
    presentation = PAGE_NODE_PRESENTATION.LIST,
    typeDefOverride = null,
}) => {

    // ----- CONTEXT ----- //

    const language = useCurrentLanguage();
    const { getType: getContextType } = useNodeTypes();
    const { theme } = useTheme();

    const normalizedPresentation = normalizePageNodePresentation(presentation);
    const getType = getTypeOverride || getContextType;
    const typeKey = String(node?.type || 'NODE').trim() || 'NODE';
    const typeDef = typeDefOverride || getType(typeKey) || null;
    const isGalleryMediaType = typeKey === 'PHOTO' || typeKey === 'VIDEO';
    const typeLabel = getNodeTypeLabel(getType, typeKey, { fallback: 'Node' });

    // ----- MEMOS ----- //

    const listItemConfig = useMemo(() => (
        resolveListViewItemConfig({
            forceDefaultFields: true,
            typeDef,
        })
    ), [typeDef]);
    const listItemData = useMemo(() => (
        buildListViewItemData({
            config: listItemConfig,
            language,
            node,
        })
    ), [language, listItemConfig, node]);
    const modifiedLabel = useMemo(() => {
        const modified = node?.dateModified || node?.dateCreated || null;
        return modified ? formatSummaryDate(modified) : '';
    }, [node?.dateCreated, node?.dateModified]);
    const CardComponent = useMemo(() => {
        return resolveComponent(typeKey, ComponentType.CARD, getType, {
            allowNullIfMissingBase: true,
            quiet: true,
        });
    }, [getType, typeKey]);

    // ----- HOOKS ----- //

    const {
        cardFieldKeys,
        schemaFieldMap,
        resolveFieldValue,
        formatFieldValue,
    } = useGalleryCardFieldFormatting({
        view: GALLERY_PREVIEW_VIEW,
        typeDef,
        language,
        nodes: node ? [node] : [],
    });

    if (!node) return null;

    if (normalizedPresentation === PAGE_NODE_PRESENTATION.GALLERY) {
        return (
            <div className="page-node-preview-container page-node-preview-container-gallery">

                {/* GALLERY FRAME MEDIA */}
                <div
                    className={[
                        'page-node-preview-gallery-frame',
                        isGalleryMediaType ? 'page-node-preview-gallery-frame-media' : 'page-node-preview-gallery-frame-data',
                    ].join(' ')}
                >
                    <GalleryCardRenderer
                      cardFieldKeys={cardFieldKeys}
                      formatFieldValue={formatFieldValue}
                      node={node}
                      resolveFieldValue={resolveFieldValue}
                      schemaFieldMap={schemaFieldMap}
                      theme={theme}
                      typeDef={typeDef}
                    />
                </div>
            </div>
        );
    }

    if (normalizedPresentation === PAGE_NODE_PRESENTATION.FEED && CardComponent) {

        return (
            <div className="page-node-preview-container page-node-preview-container-feed">

                {/* FEED HEADER */}
                <div className="page-node-preview-feed-header">

                    {/* FEED TITLE */}
                    <div
                      className="page-node-preview-feed-title"
                    >
                        {typeLabel}
                    </div>

                    {/* FEED META */}
                    {modifiedLabel ? (
                        <div
                          className="page-node-preview-feed-meta"
                        >
                            Updated {modifiedLabel}
                        </div>
                    ) : null}
                </div>

                {/* FEED CARD */}
                <div
                  className="page-node-preview-feed-card"
                >
                    <CardComponent node={node} />
                </div>
            </div>
        );
    }

    // ----- RENDER ----- //

    return (
        <div className="page-node-preview-container page-node-preview-container-list">
            <ListViewItem
              coverUrl={listItemData.coverUrl}
              subtitle={listItemData.subtitle || (modifiedLabel ? `Updated ${modifiedLabel}` : '')}
              enumPills={listItemData.enumPills}
              fieldValues={listItemData.fieldValues}
              node={node}
            />
        </div>
    );
});

// Dev Tools
PageNodePreview.displayName = 'PageNodePreview';

export default PageNodePreview;
